import { useRef } from 'react'
import { Form, Link, useSubmit } from 'react-router'
import { BrandLogo } from '#app/components/brand/logo.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	NavigationMenu,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
} from '#app/components/ui/navigation-menu.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

const links = [
	{ href: '/free-backlink-checker', label: 'Free tool' },
	{ href: '/guides', label: 'Guides' },
	{ href: '/best-tools', label: 'Best tools' },
	{ href: '/course', label: 'Course' },
]

export default function Header() {
	return (
		<header className="border-hairline border-b">
			<div className="mx-auto flex max-w-[1200px] items-center justify-between gap-3 px-5 py-[22px] sm:px-8">
				{/* Wordmark collapses to the mark alone on the narrowest screens */}
				<BrandLogo
					className="min-w-0"
					textClassName="hidden truncate min-[420px]:inline"
				/>
				<DesktopNav />
			</div>
		</header>
	)
}

function DesktopNav() {
	const user = useOptionalUser()
	return (
		<nav className="flex items-center gap-1 md:gap-6">
			<div className="hidden items-center gap-6 text-[14.5px] font-medium md:flex">
				{links.map(({ href, label }) => (
					<Link key={href} to={href} className="hover:text-ink text-[#4A4952]">
						{label}
					</Link>
				))}
			</div>

			<Popover>
				<PopoverTrigger asChild>
					<Button
						className="group md:hidden"
						variant="ghost"
						size="icon"
						aria-label="Open navigation menu"
					>
						<svg
							className="pointer-events-none"
							width={18}
							height={18}
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M4 12L20 12"
								className="origin-center -translate-y-[7px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-aria-expanded:translate-x-0 group-aria-expanded:translate-y-0 group-aria-expanded:rotate-[315deg]"
							/>
							<path
								d="M4 12H20"
								className="origin-center transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.8)] group-aria-expanded:rotate-45"
							/>
							<path
								d="M4 12H20"
								className="origin-center translate-y-[7px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-aria-expanded:translate-y-0 group-aria-expanded:rotate-[135deg]"
							/>
						</svg>
					</Button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					className="border-hairline text-ink w-44 bg-white p-1 md:hidden"
				>
					<NavigationMenu className="max-w-none *:w-full">
						<NavigationMenuList className="flex-col items-start gap-0">
							{links.map((link) => (
								<NavigationMenuItem key={link.href} className="w-full">
									<NavigationMenuLink asChild className="py-1.5">
										<Link to={link.href}>{link.label}</Link>
									</NavigationMenuLink>
								</NavigationMenuItem>
							))}
							{user ? null : (
								<NavigationMenuItem className="w-full">
									<NavigationMenuLink asChild className="py-1.5">
										<Link to="/login">Log in</Link>
									</NavigationMenuLink>
								</NavigationMenuItem>
							)}
						</NavigationMenuList>
					</NavigationMenu>
				</PopoverContent>
			</Popover>

			{user ? (
				<UserDropdown />
			) : (
				<div className="flex items-center gap-2 sm:gap-4">
					<Link
						to="/login"
						className="hover:text-ink hidden text-[14.5px] font-medium text-[#4A4952] sm:inline"
					>
						Log in
					</Link>
					<Button
						asChild
						className="bg-ink hover:bg-ink/90 rounded-[10px] text-white"
					>
						<Link to="/free-backlink-checker">Try it free</Link>
					</Button>
				</div>
			)}
		</nav>
	)
}

function UserDropdown() {
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon">
					<Icon name="user" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent
					sideOffset={1}
					align="end"
					className="border-hairline text-ink bg-white"
				>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/dashboard">
							<Icon name="layout-dashboard" size="xs">
								Dashboard
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						onSelect={(event) => {
							event.preventDefault()
							void submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon name="exit" size="xs">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
